import { components } from "@libertify/api-types/media";
import { SlideLayout } from "../types";

type SlideInputs = components["schemas"]["SlideDefinition"]["inputs"];
type PathInput = components["schemas"]["PathInput"];

type InputSpec = {
  key: string;
  inputType: "String" | "Number" | "StringList";
};

// Mirrors the per-slide *SlideInputValue schemas in libs/api-types/src/media.d.ts
// (e.g. FigureSlideInputValue, TitleSlideInputValue).
const SLIDE_LAYOUT_INPUTS: Record<SlideLayout, InputSpec[]> = {
  [SlideLayout.TITLE]: [
    { key: "title", inputType: "String" },
    { key: "subtitle", inputType: "String" },
    { key: "image", inputType: "String" },
  ],
  [SlideLayout.IMAGE]: [
    { key: "title", inputType: "String" },
    { key: "image", inputType: "String" },
  ],
  [SlideLayout.BULLET_POINT]: [
    { key: "title", inputType: "String" },
    { key: "bullet_points", inputType: "StringList" },
    { key: "image", inputType: "String" },
  ],
  [SlideLayout.FIGURE]: [
    { key: "title", inputType: "String" },
    { key: "figure", inputType: "Number" },
    { key: "unit", inputType: "String" },
    { key: "short_description", inputType: "String" },
    { key: "image", inputType: "String" },
  ],
  [SlideLayout.DOUBLE_PARAGRAPH]: [
    { key: "title", inputType: "String" },
    { key: "paragraph_1", inputType: "String" },
    { key: "paragraph_2", inputType: "String" },
    { key: "image", inputType: "String" },
  ],
  [SlideLayout.PARAGRAPH_IMAGE]: [
    { key: "title", inputType: "String" },
    { key: "text", inputType: "String" },
    { key: "image", inputType: "String" },
  ],
  [SlideLayout.QUOTE]: [
    { key: "quote", inputType: "String" },
    { key: "image", inputType: "String" },
  ],
};

const buildPathInput = (
  slideUuid: string,
  attribute: string,
  inputType: InputSpec["inputType"]
): PathInput => ({
  input_type: inputType,
  type: "Path",
  path: slideUuid,
  additional_path: null,
  index: null,
  attribute,
});

// Returns the canonical inputs dict for a slide type, with every entry
// pointing at the slide's own uuid and tagged with the matching attribute.
// Use it to overwrite the canonical keys on a saved slide so corrupted
// payloads (e.g. inputs lost after a theme application) heal on next save.
export const buildSlideInputs = (
  slideUuid: string,
  slideLayoutType: SlideLayout
): SlideInputs =>
  Object.fromEntries(
    SLIDE_LAYOUT_INPUTS[slideLayoutType].map(({ key, inputType }) => [
      key,
      buildPathInput(slideUuid, key, inputType),
    ])
  );
